import React, { useCallback, useEffect, useState } from "react";
import { Box, Tooltip, Typography } from "@mui/material";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import { useGetDatasetByIdQuery } from "@store/api/dataset-api";
import type { DatasetMetric } from "@models/Metric";
import type { VisualizationBaseCondition } from "@models/VisualizationBase";
import { useBaseConditionHiddenStatus } from "@hooks/use-base-condition-hidden-status";
import HiddenInDatasetBadge from "@components/common/HiddenInDatasetBadge";

interface MetricBaseSummaryCellProps {
  datasetId: string;
  base: DatasetMetric["base"];
  /** Question ids confirmed hidden by the catalog's single batched request
   *  covering every metric's source + condition questions at once. Passed
   *  through to each condition probe so it can skip its own per-condition
   *  question fetch. */
  knownHiddenQuestionIds?: Set<string>;
  /** Whether that batched request has settled — until it has, probes fall
   *  back to fetching their own question status to avoid a premature
   *  "not hidden" flash. Omit entirely (other call sites outside the
   *  catalog have no batched data) to always fetch per-condition, as before. */
  isQuestionsSettled?: boolean;
  /** Reports whether this metric's Custom Base references hidden content, so
   *  the catalog row can surface a single aggregated warning. */
  onHiddenStatusChange?: (hidden: boolean) => void;
}

// Invisible per-condition probe reporting hidden status up — same pattern as
// DatasetBaseBuilder.tsx's ConditionHiddenProbe, reused here since Metric's
// Custom Base conditions are the same VisualizationBaseCondition shape.
const ConditionHiddenProbe: React.FC<{
  datasetId: string;
  condition: VisualizationBaseCondition;
  knownQuestionHidden?: boolean;
  onResult: (conditionId: string, hidden: boolean) => void;
}> = ({ datasetId, condition, knownQuestionHidden, onResult }) => {
  const hidden = useBaseConditionHiddenStatus(
    datasetId,
    condition,
    knownQuestionHidden,
  );
  useEffect(() => {
    onResult(condition.id, hidden);
  }, [condition.id, hidden, onResult]);
  return null;
};

const MetricBaseSummaryCell: React.FC<MetricBaseSummaryCellProps> = ({
  datasetId,
  base,
  knownHiddenQuestionIds,
  isQuestionsSettled,
  onHiddenStatusChange,
}) => {
  const isPreconfigured = base.mode === "preconfigured";
  const isCustom = base.mode === "custom";
  const { data: dataset, isLoading, isError } = useGetDatasetByIdQuery(datasetId, {
    skip: !datasetId || !isPreconfigured,
  });

  const [hiddenMap, setHiddenMap] = useState<Record<string, boolean>>({});
  const handleConditionResult = useCallback(
    (conditionId: string, hidden: boolean) => {
      setHiddenMap((prev) =>
        prev[conditionId] === hidden ? prev : { ...prev, [conditionId]: hidden },
      );
    },
    [],
  );
  const hasHiddenConditions = isCustom && Object.values(hiddenMap).some(Boolean);

  useEffect(() => {
    onHiddenStatusChange?.(hasHiddenConditions);
  }, [hasHiddenConditions, onHiddenStatusChange]);

  const label =
    base.mode === "all_eligible"
      ? "All eligible respondents"
      : base.mode === "custom"
        ? base.name || "Custom base"
        : base.preconfiguredBaseName;

  // Confirmed invalid only once the dataset's bases resolve successfully and
  // don't include this reference — never flag on a transient load error.
  const isConfirmedInvalid =
    isPreconfigured &&
    !isLoading &&
    !isError &&
    !!dataset &&
    !(dataset.bases ?? []).some((b) => b.id === base.preconfiguredBaseId);

  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
      {isCustom &&
        base.conditions.map((condition) => (
          <ConditionHiddenProbe
            key={condition.id}
            datasetId={datasetId}
            condition={condition}
            knownQuestionHidden={
              isQuestionsSettled
                ? (knownHiddenQuestionIds?.has(condition.questionId) ?? false)
                : undefined
            }
            onResult={handleConditionResult}
          />
        ))}
      {isConfirmedInvalid && (
        <Tooltip title="This base is no longer available. Open the metric to repair it.">
          <WarningAmberIcon fontSize="small" color="warning" />
        </Tooltip>
      )}
      {hasHiddenConditions ? (
        <HiddenInDatasetBadge
          size="small"
          tooltipTitle="One or more Custom Base conditions reference a question, variable, or value that is hidden in the dataset. Open the metric to see which condition is affected."
        >
          <Typography variant="body2" sx={{ whiteSpace: "normal", maxWidth: 240 }}>
            {label}
          </Typography>
        </HiddenInDatasetBadge>
      ) : (
        <Typography variant="body2" sx={{ whiteSpace: "normal", maxWidth: 240 }}>
          {label}
        </Typography>
      )}
    </Box>
  );
};

export default MetricBaseSummaryCell;
